import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../i18n/index.js';
import { Input } from 'antd';
import { useState, type ReactElement } from 'react';

import { Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';
import { commandRequest } from '../../../agents/context/ui-command-registry.js';
import type { EntityChatAgent } from '../hooks/useEntityChat.js';

type DealStage = 'New' | 'Contacted' | 'Negotiation' | 'Won' | 'Lost';

interface DealChatCrudActionsProps {
  agent: Pick<EntityChatAgent, 'isStreaming' | 'send'>;
  dealId: string;
  disabled?: boolean;
}

const DEAL_STAGE_OPTIONS: Array<{ value: DealStage; label: string }> = [
  {
    value: 'New',
    get label() {
      return i18n.t('crm.features.map.features.ai.chat.components.dealchatcrudactions.1');
    },
  },
  {
    value: 'Contacted',
    get label() {
      return i18n.t('crm.features.map.features.ai.chat.components.dealchatcrudactions.2');
    },
  },
  {
    value: 'Negotiation',
    get label() {
      return i18n.t('crm.features.map.features.ai.chat.components.dealchatcrudactions.3');
    },
  },
  {
    value: 'Won',
    get label() {
      return i18n.t('crm.deals.stage.Won');
    },
  },
  {
    value: 'Lost',
    get label() {
      return i18n.t('crm.deals.stage.Lost');
    },
  },
];

export function DealChatCrudActions({
  agent,
  dealId,
  disabled = false,
}: DealChatCrudActionsProps): ReactElement {
  const { t } = useTranslation();
  const [noteBody, setNoteBody] = useState('');
  const [stage, setStage] = useState<DealStage>('Negotiation');
  const isBusy = disabled || agent.isStreaming;
  const trimmedNote = noteBody.trim();

  return (
    <Card
      className="rounded-xl border border-stone-200 bg-white p-0 shadow-none"
      data-testid="deal-chat-crud-actions"
    >
      <CardHeader className="px-4 pb-2 pt-4">
        <CardTitle className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          {t('crm.chat.crud_title')}
        </CardTitle>
        <p className="mt-1 text-xs leading-5 text-stone-500">{t('crm.chat.crud_hint')}</p>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-0">
        <div className="space-y-2">
          <label
            className="block text-xs font-semibold uppercase tracking-wide text-stone-500"
            htmlFor="deal-chat-note-body"
          >
            {t('crm.chat.crud_note_label')}
          </label>
          <Input.TextArea
            id="deal-chat-note-body"
            data-testid="deal-chat-crud-note-input"
            value={noteBody}
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={isBusy}
            placeholder={t('crm.chat.crud_note_placeholder')}
            onChange={(event) => setNoteBody(event.target.value)}
          />
          <Button
            type="button"
            size="sm"
            className="w-full justify-center"
            disabled={isBusy || trimmedNote.length === 0}
            data-testid="deal-chat-crud-add-note"
            onClick={() => {
              void agent.send(commandRequest('manualDealNote', { dealId, body: trimmedNote }));
              setNoteBody('');
            }}
          >
            {t('crm.chat.crud_add_note')}
          </Button>
        </div>
        <div className="space-y-2">
          <label
            className="block text-xs font-semibold uppercase tracking-wide text-stone-500"
            htmlFor="deal-chat-target-stage"
          >
            {t('crm.chat.crud_stage_label')}
          </label>
          <select
            id="deal-chat-target-stage"
            data-testid="deal-chat-crud-stage-select"
            value={stage}
            disabled={isBusy}
            className="h-9 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-xs font-medium text-stone-700"
            onChange={(event) => setStage(event.target.value as DealStage)}
          >
            {DEAL_STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="w-full justify-center border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
            disabled={isBusy}
            data-testid="deal-chat-crud-update-stage"
            onClick={() => {
              void agent.send(commandRequest('updateDealStage', { dealId, stage }));
            }}
          >
            {t('crm.chat.crud_update_stage')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
